import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Trash2, Key, RefreshCw, ShieldCheck, AlertCircle } from "lucide-react";

export default function MerchantApiKeys() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("Production");
  const [newKey, setNewKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/merchant/api-keys");
      setKeys(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Could not load API keys.");
    }
  };

  useEffect(() => { load(); }, []);

  const activeKey = keys.find((k) => !k.revoked);
  const revokedKeys = keys.filter((k) => k.revoked);

  const handleGenerateOrRotate = async () => {
    setLoading(true);
    try {
      const keyName = (name || "Production").trim();
      const { data } = await api.post("/merchant/api-keys", { name: keyName });
      setNewKey(data.key);
      setShowRotateConfirm(false);
      toast.success(activeKey ? "New API key generated. Previous key has been revoked." : "API key created successfully.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate API key.");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this active key? Any checkout or server integration using it will immediately stop working.")) return;
    try {
      await api.delete(`/merchant/api-keys/${id}`);
      toast.success("API key revoked.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not revoke API key.");
    }
  };

  return (
    <div data-testid="merchant-api-keys" className="max-w-4xl space-y-6">
      <div className="flex items-center gap-3 flex-wrap mt-3">
        <div className="pf-chip pf-chip-cyan">Integrations</div>
        <a href="/developers" className="text-xs font-semibold text-cyan-700 hover:text-cyan-800 underline underline-offset-2" data-testid="link-developer-docs">
          Read the developer docs →
        </a>
      </div>

      <div>
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-slate-900">API Key</h2>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Use this key to authenticate your server calls to <code className="font-mono text-cyan-700">/api/v1/*</code> (such as coupon validation and conversions). Pass it via header <code className="font-mono text-cyan-700">Authorization: Bearer &lt;key&gt;</code> or <code className="font-mono text-cyan-700">X-Api-Key: &lt;key&gt;</code>.
        </p>
      </div>

      {newKey && (
        <div className="pf-card p-5 bg-gradient-to-br from-cyan-50 to-emerald-50 border border-cyan-200 shadow-sm" data-testid="new-key-banner">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-800 font-bold">
            <ShieldCheck className="w-4 h-4 text-cyan-600" /> Copy your new API key now
          </div>
          <p className="text-xs text-slate-600 mt-1" data-testid="new-key-once-note">This key is shown only once — Cyannect stores only a secure hash and can't show it again. Save it in your password manager or server secrets. If you lose it, create a new key.</p>
          <div className="flex items-center gap-2 mt-3">
            <code className="font-mono text-sm bg-white/90 border border-cyan-200 px-3 py-2 rounded-lg flex-1 truncate select-all" data-testid="new-key-value">{newKey}</code>
            <button
              data-testid="new-key-copy-btn"
              onClick={() => { navigator.clipboard.writeText(newKey); toast.success("Copied to clipboard"); }}
              className="pf-btn-primary px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5"
            >
              <Copy className="w-3.5 h-3.5" /> Copy Key
            </button>
            <button onClick={() => setNewKey(null)} className="pf-btn-glass px-3 py-2 rounded-lg text-xs">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Active Key Section */}
      {activeKey ? (
        <div className="pf-card p-6 bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="active-key-card">
          <div className="flex items-center justify-between pb-4 border-b border-slate-100 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center">
                <Key className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-slate-900">{activeKey.name || "Production"}</h3>
                  <span className="pf-chip pf-chip-mint text-xs">Active</span>
                </div>
                <div className="font-mono text-xs text-slate-500 mt-0.5">
                  Preview: <span className="font-semibold text-slate-700">{activeKey.key_preview || "cyk_live_••••••••"}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowRotateConfirm(true)}
                className="pf-btn-glass px-3.5 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 text-slate-700 hover:text-cyan-700 hover:border-cyan-300"
                data-testid="rotate-key-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Roll / Rotate key
              </button>
              <button
                type="button"
                onClick={() => handleRevoke(activeKey.id)}
                className="pf-btn-glass px-3 py-1.5 rounded-xl text-xs font-semibold text-red-600 hover:bg-red-50 hover:border-red-200 flex items-center gap-1"
                data-testid={`revoke-key-${activeKey.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" /> Revoke
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 text-xs text-slate-500">
            <div>
              <span className="font-medium text-slate-700">Created:</span>{" "}
              {activeKey.created_at ? new Date(activeKey.created_at).toLocaleString() : "—"}
            </div>
            <div>
              <span className="font-medium text-slate-700">Policy:</span> Single active key (generating a new key auto-revokes the old one)
            </div>
          </div>
        </div>
      ) : (
        <div className="pf-card p-6 bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="no-active-key-card">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-slate-900">No Active API Key</h3>
              <p className="text-xs text-slate-600 mt-1">
                You do not currently have an active API key. Generate one to authenticate checkout integrations, conversions, and coupon validations.
              </p>
              <div className="mt-4 flex items-center gap-3 max-w-md">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Key name (e.g. Production)"
                  className="pf-input text-xs px-3 py-2 flex-1"
                  data-testid="new-key-name"
                />
                <button
                  onClick={handleGenerateOrRotate}
                  disabled={loading || !name.trim()}
                  className="pf-btn-primary px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-50"
                  data-testid="new-key-btn"
                >
                  {loading ? "Generating..." : "Generate Key"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rotation Confirmation Modal */}
      {showRotateConfirm && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-100 space-y-4">
            <div className="flex items-center gap-3 text-amber-600">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <RefreshCw className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Rotate API Key?</h3>
                <p className="text-xs text-slate-500">Single active key policy</p>
              </div>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Generating a new API key will <strong className="text-slate-900">immediately revoke your existing active key</strong> ({activeKey?.key_preview}). Any servers or checkouts using the old key will stop functioning until updated.
            </p>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">New Key Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pf-input w-full text-xs px-3 py-2"
                placeholder="Production"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowRotateConfirm(false)}
                className="pf-btn-glass px-4 py-2 rounded-xl text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerateOrRotate}
                disabled={loading}
                className="pf-btn-primary px-4 py-2 rounded-xl text-xs font-semibold bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-rotate-btn"
              >
                {loading ? "Rolling..." : "Confirm & Rotate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoked Keys Audit History */}
      {revokedKeys.length > 0 && (
        <div className="pf-card overflow-hidden rounded-2xl bg-white/70 border border-slate-200">
          <div className="px-5 py-3.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-600">Key History (Revoked)</h4>
            <span className="text-xs text-slate-400">{revokedKeys.length} revoked</span>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50/50 text-left uppercase tracking-widest text-slate-400">
              <tr>
                <th className="px-5 py-2.5">Name</th>
                <th className="px-5 py-2.5">Preview</th>
                <th className="px-5 py-2.5">Created</th>
                <th className="px-5 py-2.5">Revoked</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {revokedKeys.map((k) => (
                <tr key={k.id} className="text-slate-500">
                  <td className="px-5 py-2.5 font-medium text-slate-700">{k.name}</td>
                  <td className="px-5 py-2.5 font-mono text-slate-400">{k.key_preview || "—"}</td>
                  <td className="px-5 py-2.5">{k.created_at ? new Date(k.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-5 py-2.5"><span className="pf-chip text-[10px] text-red-600 bg-red-50">Revoked</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Quick Reference */}
      <div className="pf-card p-5 rounded-2xl bg-white border border-slate-200">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Quick reference</div>
        <pre className="font-mono text-xs bg-slate-900 text-slate-100 rounded-xl p-4 mt-2 overflow-x-auto" data-testid="api-keys-curl-ref">{`# Validate (Bearer header — recommended)
curl -X POST $CYANNECT_URL/api/v1/coupons/validate \\
  -H "Authorization: Bearer cyk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"coupon":"CYN-XXXXXXXX","cart_value":100}'

# Check funnel metrics (Test your API key)
curl -X GET $CYANNECT_URL/api/v1/funnel/summary \\
  -H "X-Api-Key: cyk_live_..."`}</pre>
      </div>
    </div>
  );
}

